import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

// Plugins GSAP (registration centralisée & idempotente)
(function registerGsapPluginsOnce() {
    if (typeof window === 'undefined') return;

    if (!window.__ST_REGISTERED__) {
        try {
            gsap.registerPlugin(ScrollTrigger);
            window.__ST_REGISTERED__ = true;
        } catch {
            // noop
        }
    }

    if (!window.__SPLITTEXT_REGISTERED__) {
        try {
            gsap.registerPlugin(SplitText);
            window.__SPLITTEXT_REGISTERED__ = true;
        } catch {
            // noop
        }
    }
})();

export default class AnimationPreferences {
    static init() {
        this.body = document.body;
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.animationCookieName = 'a42-ac-animations';
        this.animationsEnabled = this.readAnimationCookie();

        this.applyInitialPreferences();
        // window.addEventListener('load', this.update.bind(this));
    }

    static update() {
        setTimeout(this.updateScrollTriggers.bind(this), 500);
    }

    static handleAnimationChange(selectedValue) {
        if (selectedValue === 'a42-ac-enable-animations') {
            this.enableAnimations();
        } else {
            this.disableAnimations();
        }
    }

    static applyInitialPreferences() {
        if (this.animationsEnabled === 'a42-ac-disable-animations' || this.prefersReducedMotion) {
            this.disableAnimations();
            window.animationsEnabled = false;
            this.setRootAnimClass(false);
        } else {
            this.enableAnimations();
            window.animationsEnabled = true;
            this.setRootAnimClass(true);
        }
    }

    static setRootAnimClass(isOn) {
        if (typeof document === 'undefined') return;
        const root = document.documentElement;
        if (isOn) root.classList.add('js-anim');
        else root.classList.remove('js-anim');
    }

    static enableAnimations() {
        this.createAnimationCookie('a42-ac-enable-animations');
        window.animationsEnabled = true;
        this.setRootAnimClass(true);
    }

    static disableAnimations() {
        this.createAnimationCookie('a42-ac-disable-animations');
        window.animationsEnabled = false;
        this.setRootAnimClass(false);
        this.killScrollTriggers();
    }

    static isEnabled() {
        return this.shouldAnimate();
    }

    static killScrollTriggers() {
        const triggers = ScrollTrigger.getAll();
        if (!triggers.length) return;

        triggers.forEach((trigger) => trigger.kill());
        // console.log('All scrollTriggers killed');
    }

    static refreshScrollTriggers(location = '') {
        const triggers = ScrollTrigger.getAll();
        if (!triggers.length) return;

        triggers.forEach((trigger) => trigger.refresh());
        // console.log('All scrollTriggers refreshed', location);
    }

    static updateScrollTriggers() {
        const triggers = ScrollTrigger.getAll();
        if (!triggers.length) return;

        triggers.forEach((trigger) => trigger.update());
        // console.log('All scrollTriggers updated');
    }

    static shouldAnimate() {
        return !(
            this.animationsEnabled === 'a42-ac-disable-animations' || this.prefersReducedMotion
        );
    }

    static createAnimationCookie(value) {
        const date = new Date();
        date.setTime(date.getTime() + 180 * 24 * 60 * 60 * 1000); // 180 jours
        document.cookie = `${
            this.animationCookieName
        }=${value}; expires=${date.toUTCString()}; path=/`;
        this.animationsEnabled = value;
    }

    static readAnimationCookie() {
        const nameEQ = `${this.animationCookieName}=`;
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return 'a42-ac-enable-animations'; // Valeur par défaut
    }
}

/**
 * Helpers d’animation réutilisables dans tout le front.
 * - motionEnabled() combine cookie + prefers-reduced-motion
 * - fx.to / fx.fromTo : tombent en gsap.set si animations désactivées
 */
export const motionEnabled = () => {
    // On s’appuie sur la logique existante de la classe
    if (typeof AnimationPreferences?.shouldAnimate === 'function') {
        return AnimationPreferences.shouldAnimate();
    }
    // Fallback si la classe n’est pas initialisée
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    return window.animationsEnabled !== false && !prefersReducedMotion;
};

export const fx = {
    to(target, vars = {}) {
        if (motionEnabled()) return gsap.to(target, vars);
        gsap.set(target, stripTiming(vars));
        vars?.onComplete?.();
        return null;
    },
    fromTo(target, fromVars = {}, toVars = {}) {
        if (motionEnabled()) return gsap.fromTo(target, fromVars, toVars);
        gsap.set(target, { ...fromVars, ...stripTiming(toVars) });
        toVars?.onComplete?.();
        return null;
    },
    set(target, vars = {}) {
        // utilitaire cohérent avec gsap.set, utile même quand l’anim est on
        return gsap.set(target, vars);
    },
    timeline(vars = {}) {
        if (motionEnabled()) return gsap.timeline(vars);

        // Timeline factice pour rester chaînable
        const dummy = {
            to(target, v = {}) {
                gsap.set(target, stripTiming(v));
                v?.onComplete?.();
                return this;
            },
            fromTo(target, f = {}, t = {}) {
                gsap.set(target, { ...f, ...stripTiming(t) });
                t?.onComplete?.();
                return this;
            },
            set(target, v = {}) {
                gsap.set(target, v);
                return this;
            },
            add(callback) {
                if (typeof callback === 'function') callback();
                return this;
            },
        };
        return dummy;
    },
    matchMedia(queries) {
        if (motionEnabled()) {
            return gsap.matchMedia(queries);
        }
        // Fallback quand les animations sont désactivées
        return {
            add: () => {},
            revert: () => {},
        };
    },
    clearProps: { clearProps: 'all' },
    clearDisplay: { display: '' },
};

/**
 * Nettoie les propriétés de timing qui n’ont pas de sens pour gsap.set
 */
function stripTiming(vars = {}) {
    // eslint-disable-next-line no-unused-vars
    const { duration: _d, delay: _dl, ease: _e, stagger: _s, repeat: _r, yoyo: _y, ...rest } = vars;
    return rest;
}
